/**
 * 🏰 Mini Castles — Phase 1A frontend (claim · state · map · manual deposit)
 *
 * Lean MVP. Raiding lands in Phase 1B; the Raid button is disabled with a
 * "coming next" hint so the visual loop is already in place when combat
 * goes live.
 *
 * Routes:
 *   GET  /api/castle/state    → has_castle, castle{}, quadrants[], vault_cap, user_chits
 *   POST /api/castle/claim    → { quadrant, x, y, name }
 *   GET  /api/castle/map      → ?quadrant=south&page=0
 *   POST /api/castle/deposit  → { amount }
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Sparkles, ShieldCheck, ShieldOff, MapPin, Coins, Swords } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🌍 Owner-locked quadrants (mirrors `castle_service.QUADRANTS`).
// Only South is canon for mines today; castles can live in any quadrant.
const QUADRANT_META = {
  south: { icon: "🌅", label: "South Quadrant",  color: "border-amber-700/60 from-amber-950/60 to-black", note: "Active mining region" },
  north: { icon: "❄️", label: "North Quadrant",  color: "border-sky-700/60 from-sky-950/60 to-black",     note: "Frozen, sparsely settled" },
  east:  { icon: "🌊", label: "East Quadrant",   color: "border-emerald-700/60 from-emerald-950/60 to-black", note: "Coastal & humid" },
  west:  { icon: "🌋", label: "West Quadrant",   color: "border-rose-700/60 from-rose-950/60 to-black",   note: "Volcanic foothills" },
};

function formatErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function CastlePage() {
  const { isAuthed, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [quadrant, setQuadrant] = useState("south");
  const [loadingMap, setLoadingMap] = useState(false);
  const [raidingId, setRaidingId] = useState(null);
  const [quickRaidingId, setQuickRaidingId] = useState(null);
  const [raidResult, setRaidResult] = useState(null); // { win, chits_won, spirit_gifted, trophies_won, defender_name, defender_level, od_charged, raids_today, daily_free, daily_cap }

  const refreshState = async () => {
    try {
      const r = await axios.get(`${API}/castle/state`);
      setState(r.data);
      // After first state we know which quadrant the player chose — default
      // map view to that quadrant so they instantly see their neighbours.
      if (r.data?.castle?.location?.quadrant) {
        setQuadrant(r.data.castle.location.quadrant);
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load castle.");
    }
  };

  const refreshMap = async (q) => {
    setLoadingMap(true);
    try {
      const r = await axios.get(`${API}/castle/map`, { params: { quadrant: q, page: 0, page_size: 24 } });
      setMapData(r.data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load map.");
    } finally {
      setLoadingMap(false);
    }
  };

  useEffect(() => { if (isAuthed) refreshState(); }, [isAuthed]);
  useEffect(() => { if (isAuthed) refreshMap(quadrant); }, [isAuthed, quadrant]);

  // ⚔️🎯 Manual Castle Raid — kicks off a turn-by-turn fight at /battle
  const onRaidManually = async (target) => {
    if (target.bubble_active) {
      toast.error("This castle is inside a Protection Bubble — pick another.");
      return;
    }
    // 🛡️ Owner-locked Feb 28, 2026 — outgoing raids while bubbled LIFT
    // your own Protection Bubble. Warn before the player commits.
    const myBubbleActive = !!state?.castle?.bubble_active;
    if (myBubbleActive) {
      const ok = window.confirm(
        `⚠️ WARNING\n\nYour castle is currently inside a Protection Bubble.\n\n` +
        `Launching this raid will LIFT YOUR OWN BUBBLE — your keep will be ` +
        `open to attacks again immediately.\n\n` +
        `Tip: a friend or guild-mate could raid on your behalf without ` +
        `popping your shield (proxy raids — coming soon).\n\n` +
        `Continue with the raid?`
      );
      if (!ok) return;
    }
    // 💎 OD-COST WARNING — owner-locked Feb 2026: after the daily free raids
    // (RAID_DAILY_FREE = 5), each additional raid costs RAID_OD_COST_OVERAGE
    // OmniDollars (250). Players were accidentally burning OD past their
    // 5th attack — surface a confirm so they OPT IN to the cost every time.
    const rm = state?.raid_meta;
    if (rm?.next_raid_costs_od) {
      const remainingPaid = Math.max(0, rm.daily_cap - rm.raids_today);
      const odCost = rm.od_cost_per_raid || 250;
      const userOd = rm.user_od || 0;
      if (userOd < odCost) {
        toast.error(
          `This raid costs ${odCost} 💎 OD — you only have ${userOd}. ` +
          `Free raids reset at UTC midnight.`
        );
        return;
      }
      const ok2 = window.confirm(
        `⚠️ THIS RAID COSTS OMNI DOLLARS\n\n` +
        `You've used ${rm.raids_today} / ${rm.daily_free} free raids today.\n` +
        `Each additional raid (this one included) costs ${odCost} 💎 OD.\n\n` +
        `Your balance: ${userOd} 💎 OD\n` +
        `After this raid: ${userOd - odCost} 💎 OD\n` +
        `Paid raids left today: ${remainingPaid} (cap ${rm.daily_cap}).\n\n` +
        `Continue and spend ${odCost} OD?`
      );
      if (!ok2) return;
    }
    setRaidingId(target.owner_user_id);
    try {
      const r = await axios.post(`${API}/castle/raid-manual`, { defender_id: target.owner_user_id });
      if (r.data?.attacker_bubble_lifted) {
        toast.warning("🛡️ Your Protection Bubble was lifted by this raid.");
      }
      toast.success(`⚔️ Engaging ${target.name}'s defenses...`);
      navigate("/battle?return_to=/castle");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't engage the raid.";
      if (typeof detail === "string" && detail.includes("already have a battle")) {
        toast.message("⚔️ You have an unfinished fight — resuming it.");
        navigate("/battle?return_to=/castle");
      } else {
        toast.error(formatErr(detail));
      }
    } finally {
      setRaidingId(null);
    }
  };

  // ⚡ QUICK RAID (Phase 1A instant-resolution) — POSTs /api/castles/raid and
  // opens the loot modal directly with chits stolen + trophies gained. No
  // navigation, no turn-by-turn combat. Refreshes user + castle state so the
  // header HUD balance updates in place.
  const onQuickRaid = async (target) => {
    if (target.bubble_active) {
      toast.error("This castle is inside a Protection Bubble — pick another.");
      return;
    }
    const myBubbleActive = !!state?.castle?.bubble_active;
    if (myBubbleActive) {
      const ok = window.confirm(
        `⚠️ WARNING\n\nYour castle is currently inside a Protection Bubble.\n\n` +
        `Launching this raid will LIFT YOUR OWN BUBBLE — your keep will be ` +
        `open to attacks again immediately.\n\nContinue?`
      );
      if (!ok) return;
    }
    const rm = state?.raid_meta;
    if (rm?.next_raid_costs_od) {
      const remainingPaid = Math.max(0, rm.daily_cap - rm.raids_today);
      const odCost = rm.od_cost_per_raid || 250;
      const userOd = rm.user_od || 0;
      if (userOd < odCost) {
        toast.error(
          `This raid costs ${odCost} 💎 OD — you only have ${userOd}. ` +
          `Free raids reset at UTC midnight.`
        );
        return;
      }
      const ok2 = window.confirm(
        `⚠️ THIS RAID COSTS OMNI DOLLARS\n\n` +
        `Free raids used: ${rm.raids_today} / ${rm.daily_free}.\n` +
        `Each additional raid costs ${odCost} 💎 OD.\n\n` +
        `Your balance: ${userOd} 💎 OD\n` +
        `After this raid: ${userOd - odCost} 💎 OD\n` +
        `Paid raids left today: ${remainingPaid} (cap ${rm.daily_cap}).\n\n` +
        `Continue and spend ${odCost} OD?`
      );
      if (!ok2) return;
    }
    setQuickRaidingId(target.owner_user_id);
    try {
      const r = await axios.post(`${API}/castles/raid`, {
        target_castle_id: target.owner_user_id,
      });
      // Pop the loot modal — data-shape matches castle_service.raid_castle().
      setRaidResult({
        ...r.data,
        defender_name: r.data.defender_name || target.name,
        defender_level: r.data.defender_level || target.defender_level,
        defender_is_npc: r.data.defender_is_npc ?? !!target.is_npc,
      });
      // Refresh HUD wallet + state so top balance updates immediately.
      try { await refreshUser?.(); } catch {/* silent */}
      await refreshState();
      await refreshMap(quadrant);
    } catch (e) {
      // 🔍 Surface the actual backend reason instead of a generic fallback.
      // Order: FastAPI `detail` field → axios message → HTTP status → generic.
      const status = e?.response?.status;
      const rawDetail = e?.response?.data?.detail;
      const axiosMsg = e?.message;
      const detail =
        rawDetail
        || (status ? `Raid failed (HTTP ${status}). ${axiosMsg || ""}`.trim() : null)
        || axiosMsg
        || "Couldn't complete the raid.";
      // Console-log full error so devtools has the whole payload for triage.
      console.error("[quick-raid] failed", {status, data: e?.response?.data, message: axiosMsg});
      toast.error(formatErr(detail));
    } finally {
      setQuickRaidingId(null);
    }
  };


  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0010] via-black to-[#10001a] text-amber-100">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="castle-signin-required">
          <div className="text-6xl mb-3">🏰</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>MINI CASTLES</h1>
          <p className="text-neutral-400">Sign in to claim your keep on the WILDS.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0010] via-black to-[#10001a] text-amber-100" data-testid="castle-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6 pb-24 space-y-5">
        {/* 🏰 Page header */}
        <div className="rounded-2xl border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#1a0010] via-black/60 to-[#10001a] p-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🏰</div>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80 mb-1">Kingdom of Dragonious · The Wilds</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🏰 MINI CASTLES
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
              Stake your keep anywhere on the WILDS. Stash Chits in your vault.
              Soon, other drifters will come knocking. <span className="text-amber-300">Phase 1A · raiding goes live next.</span>
            </p>
          </div>
        </div>

        {/* My castle OR claim form */}
        {state?.has_castle ? (
          <MyCastleCard state={state} onChange={refreshState} />
        ) : (
          <ClaimCastleForm onClaimed={refreshState} userChits={state?.user_chits ?? 0} />
        )}

        {/* 🏆 Trophies + Leaderboard — only after the player has claimed a castle */}
        {state?.has_castle && <TrophyAndLeaderboard />}

        {/* Map */}
        <MapSection
          mapData={mapData}
          loading={loadingMap}
          quadrant={quadrant}
          setQuadrant={setQuadrant}
          onRaid={onRaidManually}
          onQuickRaid={onQuickRaid}
          raidingId={raidingId}
          quickRaidingId={quickRaidingId}
        />
      </main>
      {/* ⚔️ Instant Raid Loot Modal */}
      {raidResult && (
        <RaidResultModal
          result={raidResult}
          onClose={() => setRaidResult(null)}
        />
      )}
      <Footer />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 🏆 TROPHIES + SEASON LEADERBOARD (Phase 2 — Feb 2026)
// ──────────────────────────────────────────────────────────────────────
function TrophyAndLeaderboard() {
  const [trophy, setTrophy] = useState(null);
  const [lb, setLb] = useState(null);
  const [scope, setScope] = useState("global");
  const [loading, setLoading] = useState(false);

  const fetchAll = async (s) => {
    setLoading(true);
    try {
      const [tr, lbr] = await Promise.all([
        axios.get(`${API}/castle/trophies`),
        axios.get(`${API}/castle/leaderboard`, { params: { scope: s, limit: 10 } }),
      ]);
      setTrophy(tr.data);
      setLb(lbr.data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load leaderboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(scope); }, [scope]);

  return (
    <div className="rounded-2xl border-2 border-amber-700/40 bg-gradient-to-br from-amber-950/30 via-black/60 to-amber-950/20 p-5" data-testid="trophy-leaderboard-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Castle Trophies · Season {trophy?.season || "—"}</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-4xl">🏆</span>
            <span className="text-4xl font-black text-amber-200 tabular-nums" data-testid="my-trophy-count">
              {trophy?.trophies ?? "—"}
            </span>
            <span className="text-[10px] text-neutral-500 italic">no trophy loss until season end</span>
          </div>
        </div>
        <div className="flex gap-1" data-testid="leaderboard-scope-tabs">
          {["global", "guild", "friends"].map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border rounded transition ${
                scope === s
                  ? "border-amber-500 text-amber-200 bg-amber-900/30"
                  : "border-neutral-700 text-neutral-500 hover:border-amber-700/60 hover:text-amber-300"
              }`}
              data-testid={`leaderboard-tab-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-black/50 border border-amber-900/30 p-2 max-h-72 overflow-y-auto" data-testid="leaderboard-rows">
        {loading ? (
          <div className="text-center py-4 text-amber-300/60 text-xs animate-pulse">Tallying trophies…</div>
        ) : !lb?.rows?.length ? (
          <div className="text-center py-4 text-neutral-500 text-xs">
            {lb?.empty_reason === "no_guild" && "Join a guild to see the guild leaderboard."}
            {lb?.empty_reason === "no_friends" && "Add friends to see the friends leaderboard."}
            {!lb?.empty_reason && "No raids logged this season yet — go knock down a castle 🏰⚔️"}
          </div>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {lb.rows.map((row) => (
                <tr
                  key={row.user_id}
                  className={row.is_you ? "bg-amber-900/30" : ""}
                  data-testid={`leaderboard-row-${row.rank}`}
                >
                  <td className="px-2 py-1 text-amber-300 font-black tabular-nums w-10">#{row.rank}</td>
                  <td className="px-2 py-1 text-amber-100">
                    {row.is_you && <span className="text-amber-300">⭐ </span>}
                    @{row.handle}
                    <span className="text-neutral-500 ml-1 text-[10px]">Lv {row.level}</span>
                  </td>
                  <td className="px-2 py-1 text-neutral-400 text-[10px] truncate hidden sm:table-cell">{row.castle}</td>
                  <td className="px-2 py-1 text-right text-amber-200 font-black tabular-nums">
                    {row.trophies.toLocaleString()} 🏆
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {lb?.viewer_rank != null && !lb.rows?.some(r => r.is_you) && (
        <p className="text-[10px] text-neutral-500 mt-2 text-center">
          Your current rank: <span className="text-amber-300 font-bold">#{lb.viewer_rank}</span>
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 🏰 MY CASTLE
// ──────────────────────────────────────────────────────────────────────
function MyCastleCard({ state, onChange }) {
  const c = state.castle;
  const meta = QUADRANT_META[c.location.quadrant] || QUADRANT_META.south;
  const fillPct = Math.min(100, Math.round((c.chit_vault / state.vault_cap) * 100));
  return (
    <div className={`rounded-2xl border-2 ${meta.color} bg-gradient-to-br p-6`} data-testid="my-castle-card">
      <div className="flex flex-col md:flex-row gap-5">
        <div className="md:w-1/2 space-y-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
            <span>{meta.icon} {meta.label}</span>
            <span className="text-neutral-500">·</span>
            <MapPin size={11} /> ({c.location.x}, {c.location.y})
          </div>
          <h2 className="text-3xl font-black text-amber-200" data-testid="my-castle-name" style={{ fontFamily: "Anton, sans-serif" }}>
            {c.name.toUpperCase()}
          </h2>
          <p className="text-xs text-amber-100/70 italic">{meta.note}</p>
          <div className="flex items-center gap-3 text-xs mt-3">
            {c.bubble_active ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-900/40 border border-sky-500/60 text-sky-200"
                data-testid="bubble-active"
                title="⚠️ Launching an outgoing raid will lift this bubble immediately."
              >
                <ShieldCheck size={12} /> Protection Bubble ACTIVE
                <span className="ml-1 text-[9px] text-sky-300/80">⚠️ raiding lifts it</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-neutral-900/60 border border-neutral-700 text-neutral-400" data-testid="bubble-inactive">
                <ShieldOff size={12} /> Raidable
              </span>
            )}
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-400">Raids received: <span className="text-amber-200 font-bold">{c.raid_count_received}</span></span>
          </div>
        </div>

        {/* 🪙 VAULT */}
        <div className="md:w-1/2 space-y-3">
          <div className="rounded-xl bg-black/60 border border-[#D4AF37]/30 p-4">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80">Chit Vault</span>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">cap {state.vault_cap.toLocaleString()}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <Coins size={20} className="text-[#D4AF37]" />
              <div className="text-3xl font-black text-[#D4AF37]" data-testid="vault-amount">
                {(c.chit_vault || 0).toLocaleString()}
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-zinc-900 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#DC2626] transition-[width] duration-500"
                style={{ width: `${fillPct}%` }}
                data-testid="vault-fill"
              />
            </div>
            <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
              Wallet: <span className="text-amber-200">{(state.user_chits || 0).toLocaleString()} 🪙</span>.
              Phase 1B will auto-route 5% of every Chit you earn into this vault.
            </p>
          </div>
          <DepositForm onDeposited={onChange} walletChits={state.user_chits || 0} />
        </div>
      </div>
    </div>
  );
}

function DepositForm({ onDeposited, walletChits }) {
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(amt, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive amount."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/castle/deposit`, { amount: n });
      toast.success(`🪙 Banked ${r.data.moved.toLocaleString()} Chits.${r.data.skipped ? ` (${r.data.skipped.toLocaleString()} skipped — vault cap)` : ""}`);
      setAmt("");
      onDeposited?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="deposit-form">
      <input
        type="number"
        min="1"
        value={amt}
        onChange={(e) => setAmt(e.target.value)}
        placeholder={`Bank Chits (wallet: ${walletChits.toLocaleString()})`}
        className="flex-1 bg-black/60 border border-[#D4AF37]/30 px-3 py-2 text-amber-100 placeholder-neutral-600 focus:outline-none focus:border-[#D4AF37] text-sm"
        data-testid="deposit-amount-input"
      />
      <button
        type="submit"
        disabled={busy}
        className="px-4 py-2 bg-[#D4AF37] text-black font-bold hover:bg-amber-300 disabled:opacity-50 transition text-sm flex items-center gap-1"
        data-testid="deposit-submit"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        Bank
      </button>
    </form>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 🏰 CLAIM
// ──────────────────────────────────────────────────────────────────────
function ClaimCastleForm({ onClaimed, userChits }) {
  const [quadrant, setQuadrant] = useState("south");
  const [x, setX] = useState(50);
  const [y, setY] = useState(50);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name your castle."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/castle/claim`, { quadrant, x: parseInt(x), y: parseInt(y), name: name.trim() });
      toast.success(`🏰 ${name.trim()} stands at (${x}, ${y}).`);
      onClaimed?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-[#D4AF37]/40 bg-gradient-to-br from-black via-[#1a0010]/40 to-black p-6" data-testid="claim-form">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-1">Stake Your Keep</div>
      <h2 className="text-2xl font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
        BUILD YOUR CASTLE
      </h2>
      <p className="text-xs text-amber-100/70 mb-4 leading-relaxed">
        Pick a quadrant + coordinates. One castle per drifter — no relocation in V1, so choose with intent.
        Wallet: <span className="text-amber-200 font-bold">{userChits.toLocaleString()} 🪙</span>
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1">Castle Name</label>
          <input
            type="text"
            value={name}
            maxLength={32}
            onChange={(e) => setName(e.target.value)}
            placeholder="Phoenix Keep"
            className="w-full bg-black/60 border border-[#D4AF37]/30 px-3 py-2 text-amber-100 placeholder-neutral-600 focus:outline-none focus:border-[#D4AF37]"
            data-testid="claim-name"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1">Quadrant</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(QUADRANT_META).map(([k, m]) => (
              <button
                type="button"
                key={k}
                onClick={() => setQuadrant(k)}
                data-testid={`claim-quad-${k}`}
                className={`p-2 rounded-md border-2 text-xs transition ${quadrant === k ? "border-[#D4AF37] bg-[#D4AF37]/10 text-amber-200" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"}`}
              >
                <div className="text-xl">{m.icon}</div>
                <div className="font-bold uppercase">{k}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1">X (0–99)</label>
            <input type="number" min="0" max="99" value={x} onChange={(e) => setX(e.target.value)}
              className="w-full bg-black/60 border border-[#D4AF37]/30 px-3 py-2 text-amber-100 focus:outline-none focus:border-[#D4AF37]" data-testid="claim-x" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1">Y (0–99)</label>
            <input type="number" min="0" max="99" value={y} onChange={(e) => setY(e.target.value)}
              className="w-full bg-black/60 border border-[#D4AF37]/30 px-3 py-2 text-amber-100 focus:outline-none focus:border-[#D4AF37]" data-testid="claim-y" />
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full px-4 py-3 bg-gradient-to-r from-[#DC2626] to-[#D4AF37] text-black font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-50 transition flex items-center justify-center gap-2"
          data-testid="claim-submit"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Claim This Land
        </button>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 🗺️ MAP — other castles in the quadrant
// ──────────────────────────────────────────────────────────────────────
function MapSection({ mapData, loading, quadrant, setQuadrant, onRaid, onQuickRaid, raidingId, quickRaidingId }) {
  const meta = QUADRANT_META[quadrant];
  return (
    <div className="rounded-2xl border-2 border-purple-700/40 bg-gradient-to-br from-[#10001a] via-black/70 to-[#10001a] p-6" data-testid="map-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/80">Raidable Drifters</div>
          <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            {meta.icon} {meta.label.toUpperCase()}
          </h2>
        </div>
        <div className="flex gap-1">
          {Object.keys(QUADRANT_META).map((k) => (
            <button
              key={k}
              onClick={() => setQuadrant(k)}
              data-testid={`map-quad-${k}`}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest border transition ${quadrant === k ? "border-[#D4AF37] bg-[#D4AF37]/10 text-amber-200" : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}
            >{k}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-purple-300 animate-pulse" data-testid="map-loading">Scouting the wilds…</div>
      ) : !mapData?.castles?.length ? (
        <div className="text-center py-10 text-neutral-500" data-testid="map-empty">
          No castles in this quadrant yet. Be the first to settle here.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="map-grid">
          {mapData.castles.map((c) => (
            <RaidableCastleCard
              key={c.owner_user_id}
              c={c}
              onRaid={onRaid}
              onQuickRaid={onQuickRaid}
              raiding={raidingId === c.owner_user_id}
              quickRaiding={quickRaidingId === c.owner_user_id}
            />
          ))}
        </div>
      )}
      {mapData?.total > 0 && (
        <p className="text-[10px] text-neutral-600 text-center mt-3" data-testid="map-footer-stats">
          Showing {mapData.castles.length} of {mapData.total} castles in this quadrant.
          {mapData.viewer_level != null && (
            <span className="ml-2 text-amber-400/70">
              · 🎯 Raid range: Lv {Math.max(1, mapData.viewer_level - (mapData.level_range || 9))}–{mapData.viewer_level + (mapData.level_range || 9)}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function RaidableCastleCard({ c, onRaid, onQuickRaid, raiding, quickRaiding }) {
  const outOfRange = !!c.out_of_range;
  const busy = raiding || quickRaiding;
  const disabled = c.bubble_active || outOfRange || busy || !onRaid;
  const quickDisabled = c.bubble_active || outOfRange || busy || !onQuickRaid;
  const isNpc = !!c.is_npc;
  const lvl = c.defender_level || 1;
  // Level badge color tier: green (easy) → yellow (medium) → orange (hard) → red (deadly)
  const lvlColor =
    lvl >= 40 ? "bg-red-900/60 border-red-500/70 text-red-200"
    : lvl >= 25 ? "bg-orange-900/50 border-orange-500/70 text-orange-200"
    : lvl >= 10 ? "bg-yellow-900/50 border-yellow-500/70 text-yellow-200"
    : "bg-emerald-900/50 border-emerald-500/70 text-emerald-200";
  return (
    <div
      className={`rounded-xl border bg-black/50 p-3 transition ${
        outOfRange ? "border-neutral-800 opacity-50"
        : isNpc ? "border-fuchsia-800/50 hover:border-fuchsia-500"
        : "border-purple-800/50 hover:border-purple-500"
      }`}
      data-testid={`raid-target-${c.owner_user_id}`}
    >
      <div className="flex items-start justify-between mb-1 gap-1">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {isNpc && (
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded border border-fuchsia-500/70 bg-fuchsia-950/60 text-fuchsia-200 leading-none shrink-0" data-testid="raid-target-npc-badge">
              🤖 NPC
            </span>
          )}
          <h3 className="text-sm font-black text-amber-100 truncate" data-testid="raid-target-name">{c.name}</h3>
        </div>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full border leading-none shrink-0 ${lvlColor}`} data-testid="raid-target-level">
          Lv {lvl}
        </span>
      </div>
      <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-2">
        <span className="truncate">@{c.owner_handle} · ({c.location.x}, {c.location.y})</span>
        {c.bubble_active && (
          <span className="text-sky-300 flex items-center gap-1 shrink-0" data-testid="raid-target-bubble">
            <ShieldCheck size={10} /> Bubble
          </span>
        )}
        {outOfRange && !c.bubble_active && (
          <span className="text-red-400 flex items-center gap-1 shrink-0" data-testid="raid-target-oor">
            🚫 Out of range
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-[#D4AF37] text-sm font-bold">
          <Coins size={12} /> {(c.chit_vault || 0).toLocaleString()}
        </div>
        <div className="flex items-center gap-1">
          {/* ⚡ Quick Raid — Phase 1A instant resolution */}
          <button
            onClick={() => onQuickRaid && onQuickRaid(c)}
            disabled={quickDisabled}
            title={
              c.bubble_active ? "Inside a Protection Bubble"
              : outOfRange ? "Out of range — within ±9 levels only"
              : "Quick Raid — instant loot, no combat"
            }
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1 transition ${
              quickDisabled
                ? "border-red-900/50 text-red-700/60 cursor-not-allowed"
                : "border-purple-500/70 text-purple-200 hover:bg-purple-900/40 hover:text-purple-100"
            }`}
            data-testid="quick-raid-button"
          >
            {quickRaiding ? (
              <>
                <Loader2 size={11} className="animate-spin" /> Raiding…
              </>
            ) : (
              <>⚡ Raid</>
            )}
          </button>
          {/* 🎯 Manual Raid — turn-by-turn */}
          <button
            onClick={() => onRaid && onRaid(c)}
            disabled={disabled}
            title={
              c.bubble_active ? "Inside a Protection Bubble"
              : outOfRange ? "Out of range — within ±9 levels only"
              : "Engage in manual turn-by-turn combat"
            }
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1 transition ${
              disabled
                ? "border-red-900/50 text-red-700/60 cursor-not-allowed"
                : "border-amber-600/60 text-amber-300 hover:bg-amber-900/30 hover:text-amber-200"
            }`}
            data-testid="raid-button"
          >
            <Swords size={11} /> {raiding ? "Engaging..." : "🎯"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ⚔️ RAID RESULT MODAL — Phase 1A loot summary
// ──────────────────────────────────────────────────────────────────────
function RaidResultModal({ result, onClose }) {
  const win = !!result?.win;
  const chitsWon = Number(result?.chits_won || 0);
  const trophiesWon = Number(result?.trophies_won || 0);
  const spiritWon = Number(result?.spirit_gifted || 0);
  const odCharged = Number(result?.od_charged || 0);
  const raidsToday = result?.raids_today;
  const dailyFree = result?.daily_free;
  const dailyCap = result?.daily_cap;
  const attackerBubbleLifted = !!result?.attacker_bubble_lifted;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="raid-result-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md rounded-2xl border-2 p-6 bg-gradient-to-br shadow-2xl ${
          win
            ? "border-amber-400/70 from-amber-950/80 via-black/70 to-amber-950/60"
            : "border-red-700/60 from-red-950/80 via-black/70 to-red-950/60"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="text-5xl mb-2">{win ? "⚔️" : "💀"}</div>
          <div
            className={`text-[10px] uppercase tracking-[0.4em] font-bold ${
              win ? "text-amber-300" : "text-red-300"
            }`}
          >
            {win ? "Raid Victorious" : "Raid Repelled"}
          </div>
          <h2
            className="text-3xl font-black mt-1"
            style={{ fontFamily: "Anton, sans-serif" }}
            data-testid="raid-result-headline"
          >
            {win ? "YOU BROKE THE VAULT" : "THEY HELD THE GATE"}
          </h2>
          <p className="text-xs text-neutral-400 mt-1">
            vs <span className="text-amber-200 font-bold">{result?.defender_name || "?"}</span>{" "}
            <span className="text-neutral-500">· Lv {result?.defender_level || "?"}</span>
            {result?.defender_is_npc && (
              <span className="ml-1 text-fuchsia-300">· 🤖 NPC</span>
            )}
          </p>
        </div>

        {win ? (
          <div className="space-y-2 mb-4">
            <LootRow
              icon={<Coins size={16} className="text-[#D4AF37]" />}
              label="Chits stolen"
              value={chitsWon}
              testId="raid-result-chits"
              tone="amber"
            />
            <LootRow
              icon={<span className="text-base leading-none">🏆</span>}
              label="Trophies gained"
              value={trophiesWon}
              testId="raid-result-trophies"
              tone="amber"
            />
            <LootRow
              icon={<span className="text-base leading-none">👊</span>}
              label="Brawler Spirit"
              value={spiritWon}
              testId="raid-result-spirit"
              tone="rose"
            />
          </div>
        ) : (
          <p
            className="text-sm text-red-200 text-center py-3 mb-4"
            data-testid="raid-result-loss-message"
          >
            The defenders held firm. No loot this run — but the daily counter still ticked.
          </p>
        )}

        {/* Meta footer: OD spent, raids-today progress, own-bubble lift */}
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 border-t border-neutral-800 pt-3 space-y-1">
          {odCharged > 0 && (
            <div className="flex items-center justify-between" data-testid="raid-result-od-charged">
              <span>OD spent</span>
              <span className="text-red-300 font-bold tabular-nums">-{odCharged}</span>
            </div>
          )}
          {raidsToday != null && dailyCap != null && (
            <div className="flex items-center justify-between" data-testid="raid-result-count">
              <span>Raids today</span>
              <span className="text-amber-200 font-bold tabular-nums">
                {raidsToday} / {dailyCap}
                {dailyFree != null && raidsToday > dailyFree && (
                  <span className="text-red-400 ml-1">(paid)</span>
                )}
              </span>
            </div>
          )}
          {attackerBubbleLifted && (
            <div className="text-amber-300 text-center pt-1" data-testid="raid-result-bubble-lift">
              🛡️ Your Protection Bubble was lifted by this raid.
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="raid-result-close"
          className="w-full mt-5 px-4 py-2 bg-[#D4AF37] hover:bg-amber-300 text-black font-black uppercase tracking-widest text-sm transition"
        >
          Continue Raiding
        </button>
      </div>
    </div>
  );
}

function LootRow({ icon, label, value, testId, tone }) {
  const toneClasses = tone === "rose"
    ? "text-rose-200"
    : "text-amber-200";
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-black/40 rounded border border-neutral-800">
      <div className="flex items-center gap-2 text-neutral-300 text-sm">
        {icon}
        <span>{label}</span>
      </div>
      <span
        className={`font-black tabular-nums text-lg ${toneClasses}`}
        data-testid={testId}
      >
        +{Number(value || 0).toLocaleString()}
      </span>
    </div>
  );
}
